'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

interface Position {
  symbol: string;
  side: 'LONG' | 'SHORT';
  quantity: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
  liquidationPrice?: number;
  notional: number;
  model?: string;
}

interface ModelPositions {
  model: string;
  positions: Position[];
  totalUnrealizedPnl: number;
  availableCash: number;
}

export function Positions() {
  const { t } = useLanguage();
  const [modelPositions, setModelPositions] = useState<ModelPositions[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState<string>('ALL');

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const response = await fetch('/api/bot');
        const data = await response.json();

        const models: ModelPositions[] = [];

        // DeepSeek positions
        if (data.deepseek?.status?.accountState) {
          const deepseekState = data.deepseek.status.accountState;
          models.push({
            model: 'DeepSeek-V3',
            positions: deepseekState.positions || [],
            totalUnrealizedPnl: (deepseekState.positions || []).reduce(
              (sum: number, p: Position) => sum + p.unrealizedPnl, 0
            ),
            availableCash: deepseekState.availableMargin || 0
          });
        }

        // Doubao positions
        if (data.doubao?.status?.accountState) {
          const doubaoState = data.doubao.status.accountState;
          models.push({
            model: 'Doubao',
            positions: doubaoState.positions || [],
            totalUnrealizedPnl: (doubaoState.positions || []).reduce(
              (sum: number, p: Position) => sum + p.unrealizedPnl, 0
            ),
            availableCash: doubaoState.availableMargin || 0
          });
        }

        // Qwen positions
        if (data.qwen?.status?.accountState) {
          const qwenState = data.qwen.status.accountState;
          models.push({
            model: 'Qwen',
            positions: qwenState.positions || [],
            totalUnrealizedPnl: (qwenState.positions || []).reduce(
              (sum: number, p: Position) => sum + p.unrealizedPnl, 0
            ),
            availableCash: qwenState.availableMargin || 0
          });
        }

        setModelPositions(models);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch positions:', error);
        setLoading(false);
      }
    };

    fetchPositions();
    const interval = setInterval(fetchPositions, 3000);

    return () => clearInterval(interval);
  }, []);

  const filteredModels = selectedModel === 'ALL'
    ? modelPositions
    : modelPositions.filter(m => m.model === selectedModel);

  const allModels = ['ALL', ...modelPositions.map(m => m.model)];

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-xs text-gray-500">{t.common.loadingPositions}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-black">
      {/* Filter Header */}
      <div className="border-b border-white/10 p-4">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {t.common.filter}:
          </span>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="border border-white/20 bg-black px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-white focus:border-white focus:outline-none"
          >
            {allModels.map(model => (
              <option key={model} value={model}>
                {model === 'ALL' ? t.common.allModels : model}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Positions by Model */}
      <div className="flex-1 overflow-y-auto">
        {filteredModels.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-gray-500">
            <div className="font-mono text-xs">{t.positions.noPositions}</div>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {filteredModels.map((modelData, modelIndex) => (
              <motion.div
                key={modelData.model}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: modelIndex * 0.1 }}
                className="border border-white/10 bg-black"
              >
                {/* Model Header */}
                <div className="border-b border-white/10 bg-white/5 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${
                        modelData.model === 'DeepSeek-V3' ? 'bg-blue-500' :
                        modelData.model === 'Doubao' ? 'bg-purple-500' : 'bg-orange-500'
                      }`}></div>
                      <span className="font-mono text-xs font-semibold uppercase tracking-wider text-white">
                        {modelData.model}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[10px] text-gray-400">
                        {t.common.totalUnrealizedPnl}:
                      </div>
                      <div className={`font-mono text-sm font-bold ${
                        modelData.totalUnrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {modelData.totalUnrealizedPnl >= 0 ? '+' : ''}${modelData.totalUnrealizedPnl.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Positions Table */}
                {modelData.positions.length === 0 ? (
                  <div className="p-6 text-center font-mono text-xs text-gray-500">
                    {t.positions.noPositions}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-white/10">
                        <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-gray-400">
                          <th className="p-3">{t.common.side}</th>
                          <th className="p-3">{t.common.coin}</th>
                          <th className="p-3">{t.common.leverage}</th>
                          <th className="p-3">{t.common.notional}</th>
                          <th className="p-3">{t.common.exitPlan}</th>
                          <th className="p-3 text-right">{t.positions.headers.pnl}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10 font-mono">
                        {modelData.positions.map((pos, index) => (
                          <tr key={`${pos.symbol}-${index}`} className="hover:bg-white/5">
                            <td className="p-3">
                              <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${
                                pos.side === 'LONG'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-orange-500/20 text-orange-400'
                              }`}>
                                {pos.side}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-1">
                                <span className="text-yellow-400">●</span>
                                <span className="font-semibold text-white">{pos.symbol}</span>
                              </div>
                            </td>
                            <td className="p-3 text-gray-300">{pos.leverage}X</td>
                            <td className="p-3 text-gray-300">${pos.notional?.toLocaleString() || pos.quantity * pos.currentPrice}</td>
                            <td className="p-3">
                              <button className="border border-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase hover:bg-white hover:text-black">
                                {t.common.view}
                              </button>
                            </td>
                            <td className={`p-3 text-right font-semibold ${
                              pos.unrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {pos.unrealizedPnl >= 0 ? '+' : ''}${pos.unrealizedPnl.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Available Cash */}
                <div className="border-t border-white/10 bg-white/5 p-3">
                  <div className="font-mono text-[10px] text-gray-400">
                    {t.common.availableCash}: <span className="text-white">${modelData.availableCash.toFixed(2)}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
